/**
 * @veltro/shared — Zod form schemas
 *
 * Rules:
 * - id, tenantId, createdAt, updatedAt, createdBy, updatedBy are NEVER included (server-managed)
 * - Read-only computed fields (assetCount, locationName, assetName, etc.) are NEVER included
 * - All schemas export a companion FormValues type via z.infer
 */

import { z } from "zod";

// ─────────────────────────────────────────────────────────────────────────────
// Location
// ─────────────────────────────────────────────────────────────────────────────

export const LocationSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  type: z.enum(["site", "building", "floor", "area", "room", "other"]),
  status: z.enum(["active", "inactive"]),
  parentId: z.string().optional(),
  code: z.string().max(50).optional(),
  address: z.string().max(255).optional(),
  gpsLat: z.number().min(-90).max(90).optional(),
  gpsLng: z.number().min(-180).max(180).optional(),
  photoUrls: z.array(z.string()).optional(),
});

export type LocationFormValues = z.infer<typeof LocationSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Team
// ─────────────────────────────────────────────────────────────────────────────

export const TeamSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  description: z.string().max(500).optional(),
});

export type TeamFormValues = z.infer<typeof TeamSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Asset
// ─────────────────────────────────────────────────────────────────────────────

export const AssetSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  status: z.enum(["active", "inactive", "decommissioned", "under_repair"]),
  locationId: z.string().optional(),
  parentId: z.string().optional(),
  code: z.string().max(50).optional(),
  serialNumber: z.string().max(100).optional(),
  model: z.string().max(100).optional(),
  manufacturer: z.string().max(100).optional(),
  category: z.string().max(100).optional(),
  purchaseDate: z.string().optional(),
  warrantyExpiry: z.string().optional(),
  gpsLat: z.number().min(-90).max(90).optional(),
  gpsLng: z.number().min(-180).max(180).optional(),
});

export type AssetFormValues = z.infer<typeof AssetSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// User
// ─────────────────────────────────────────────────────────────────────────────

export const UserSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  email: z.string().email("Valid email required"),
  licenseType: z.enum(["operator", "full_user"]),
  role: z.enum(["admin", "manager", "member"]),
  status: z.enum(["active", "inactive"]),
  teamId: z.string().optional(),
});

export type UserFormValues = z.infer<typeof UserSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Checklist Template
// ─────────────────────────────────────────────────────────────────────────────

export const ChecklistItemSchema = z.object({
  id: z.string().min(1),
  type: z.enum([
    "yes_no", "pass_fail", "safe_unsafe",
    "multiple_choice", "multi_select",
    "text_short", "text_long", "text_note",
    "number", "numeric", "slider", "temperature",
    "date", "datetime",
    "photo", "photo_required", "signature", "annotation",
    "barcode_scan", "location_pin", "person", "asset_lookup",
    "section_header", "instruction",
  ]),
  label: z.string().min(1, "Item label is required").max(255),
  description: z.string().max(500).optional(),
  required: z.boolean(),
  sortOrder: z.number().int().min(0),
  options: z.array(z.string()).optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  minDate: z.string().optional(),
  maxDate: z.string().optional(),
  photoRequired: z.boolean().optional(),
  actionRequired: z.boolean().optional(),
  score: z.number().optional(),
});

export const ChecklistTemplateSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  description: z.string().max(500).optional(),
  items: z.array(ChecklistItemSchema),
});

export type ChecklistItemFormValues = z.infer<typeof ChecklistItemSchema>;
export type ChecklistTemplateFormValues = z.infer<typeof ChecklistTemplateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Work Order
// ─────────────────────────────────────────────────────────────────────────────

export const WorkOrderAssigneeSchema = z.object({
  id: z.string().min(1),
  type: z.enum(["user", "team"]),
  name: z.string().min(1),
});

export const WorkOrderSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  type: z.enum(["corrective", "preventive", "inspection", "emergency", "safety"]),
  priority: z.enum(["critical", "high", "medium", "low"]),
  status: z.enum(["open", "in_progress", "on_hold", "completed", "closed"]),
  assetId: z.string().optional(),
  locationId: z.string().optional(),
  description: z.string().max(2000).optional(),
  dueDate: z.string().optional(),
  slaTargetHours: z.number().int().min(0).optional(),
  assignees: z.array(WorkOrderAssigneeSchema),
  resolutionNotes: z.string().max(2000).optional(),
  rootCause: z.string().max(2000).optional(),
});

export type WorkOrderFormValues = z.infer<typeof WorkOrderSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// PM Schedule
// ─────────────────────────────────────────────────────────────────────────────

export const PmScheduleSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  frequency: z.enum(["daily", "weekly", "biweekly", "monthly", "quarterly", "semiannual", "annual", "custom"]),
  status: z.enum(["active", "inactive"]),
  nextDueDate: z.string().min(1, "Next due date is required"),
  leadDays: z.number().int().min(0).max(365),
  assetId: z.string().optional(),
  locationId: z.string().optional(),
  checklistTemplateId: z.string().optional(),
  workOrderPriority: z.enum(["critical", "high", "medium", "low"]).optional(),
  description: z.string().max(2000).optional(),
  cronExpression: z.string().optional(),
  seasonalStartDate: z.string().optional(),
  seasonalEndDate: z.string().optional(),
  assignees: z.array(WorkOrderAssigneeSchema),
});

export type PmScheduleFormValues = z.infer<typeof PmScheduleSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Labor Log
// ─────────────────────────────────────────────────────────────────────────────

export const LaborLogSchema = z.object({
  workOrderId: z.string().min(1, "Work order is required"),
  userId: z.string().min(1, "User is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

export type LaborLogFormValues = z.infer<typeof LaborLogSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection Item (nested within a section)
// ─────────────────────────────────────────────────────────────────────────────

export const ConditionalRuleSchema = z.object({
  id: z.string().min(1),
  sourceItemId: z.string().min(1),
  operator: z.enum(["equals", "not_equals", "contains", "greater_than", "less_than"]),
  value: z.union([z.string(), z.number(), z.boolean()]),
  action: z.enum(["show", "hide", "require", "flag"]),
});

// ─── Enhanced Template Builder Schemas ───────────────────────────────────────

export const ResponseOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string(),
  score: z.number(),
  flagged: z.boolean(),
  sortOrder: z.number(),
});

export const ResponseSetSchema = z.object({
  id: z.string(),
  name: z.string(),
  scoringEnabled: z.boolean(),
  options: z.array(ResponseOptionSchema),
  isGlobal: z.boolean(),
});

export const LogicConditionSchema = z.object({
  operator: z.string(),
  value: z.union([z.string(), z.number(), z.array(z.string())]),
  valueTo: z.number().optional(),
});

export const LogicTriggerSchema = z.object({
  type: z.string(),
  notifyConfig: z
    .object({ userIds: z.array(z.string()), message: z.string().optional() })
    .optional(),
});

export const ConditionalLogicSchema = z.object({
  id: z.string(),
  condition: LogicConditionSchema,
  triggers: z.array(LogicTriggerSchema),
});

// ─────────────────────────────────────────────────────────────────────────────

export const InspectionItemSchema: z.ZodType<any> = z.object({
  id: z.string().min(1),
  sectionId: z.string().min(1),
  type: z.enum([
    "yes_no",
    "pass_fail",
    "safe_unsafe",
    "multiple_choice",
    "multi_select",
    "text_short",
    "text_long",
    "number",
    "slider",
    "temperature",
    "date",
    "datetime",
    "photo",
    "signature",
    "annotation",
    "barcode_scan",
    "location_pin",
    "person",
    "asset_lookup",
    "section_header",
    "instruction",
  ]),
  label: z.string().min(1, "Item label is required").max(255),
  description: z.string().max(500).optional(),
  required: z.boolean(),
  sortOrder: z.number().int().min(0),
  score: z.number().optional(),
  options: z.array(z.string()).optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  photoRequired: z.boolean().optional(),
  actionRequired: z.boolean().optional(),
  conditionalRules: z.array(ConditionalRuleSchema).optional(),
  // Enhanced template builder fields
  parentItemId: z.string().optional(),
  responseSetId: z.string().optional(),
  responseOptions: z.array(ResponseOptionSchema).optional(),
  increment: z.number().optional(),
  numberFormat: z.enum(["number", "temperature", "calculation"]).optional(),
  unit: z.string().optional(),
  textFormat: z.enum(["short", "paragraph"]).optional(),
  noteRequired: z.boolean().optional(),
  logic: z.array(ConditionalLogicSchema).optional(),
  children: z.lazy(() => z.array(InspectionItemSchema)).optional(),
});

export type InspectionItemFormValues = z.infer<typeof InspectionItemSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection Section (nested within a template)
// ─────────────────────────────────────────────────────────────────────────────

export const InspectionSectionSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1, "Section title is required").max(150),
  sortOrder: z.number().int().min(0),
  items: z.array(InspectionItemSchema),
  repeatEnabled: z.boolean().optional(),
  parentSectionId: z.string().optional(),
  isTitlePage: z.boolean().optional(),
});

export type InspectionSectionFormValues = z.infer<typeof InspectionSectionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection Template
// ─────────────────────────────────────────────────────────────────────────────

export const InspectionTemplateSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  description: z.string().max(2000).optional(),
  category: z.string().max(100).optional(),
  version: z.number().int().min(1),
  status: z.enum(["draft", "published", "archived"]),
  scoringEnabled: z.boolean(),
  maxScore: z.number().optional(),
  passingScore: z.number().optional(),
  sections: z.array(InspectionSectionSchema),
  tags: z.array(z.string()).optional(),
  responseSets: z.array(ResponseSetSchema).optional(),
});

export type InspectionTemplateFormValues = z.infer<typeof InspectionTemplateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection Response (nested within an inspection)
// ─────────────────────────────────────────────────────────────────────────────

export const InspectionResponseSchema = z.object({
  itemId: z.string().min(1),
  sectionId: z.string().min(1),
  flagged: z.boolean(),
  skipped: z.boolean(),
  notes: z.string().max(2000).optional(),
  photoUrls: z.array(z.string()).optional(),
  score: z.number().optional(),
  answeredAt: z.string().optional(),
  answeredBy: z.string().optional(),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional().nullable(),
});

export type InspectionResponseFormValues = z.infer<typeof InspectionResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection (executed instance)
// ─────────────────────────────────────────────────────────────────────────────

export const InspectionSchema = z.object({
  templateId: z.string().min(1, "Template is required"),
  locationId: z.string().optional(),
  assetId: z.string().optional(),
  conductedBy: z.string().min(1),
  conductedByName: z.string().min(1),
  status: z.enum(["in_progress", "completed", "requires_approval", "approved", "rejected"]),
  startedAt: z.string().min(1),
  completedAt: z.string().optional(),
  templateName: z.string().min(1),
  templateVersion: z.number().int().min(1),
  responses: z.array(InspectionResponseSchema),
  contributors: z.array(z.object({
    userId: z.string(),
    userName: z.string(),
  })).optional(),
  gpsLat: z.number().min(-90).max(90).optional(),
  gpsLng: z.number().min(-180).max(180).optional(),
});

export type InspectionFormValues = z.infer<typeof InspectionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Action (corrective action)
// ─────────────────────────────────────────────────────────────────────────────

export const ActionSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).optional(),
  priority: z.enum(["high", "medium", "low"]),
  status: z.enum(["to_do", "in_progress", "done", "overdue", "cannot_do"]),
  inspectionId: z.string().optional(),
  inspectionItemId: z.string().optional(),
  locationId: z.string().optional(),
  assetId: z.string().optional(),
  assigneeId: z.string().optional(),
  dueDate: z.string().optional(),
  completedAt: z.string().optional(),
  photoUrls: z.array(z.string()).optional(),
});

export type ActionFormValues = z.infer<typeof ActionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Inspection Schedule
// ─────────────────────────────────────────────────────────────────────────────

export const InspectionScheduleSchema = z.object({
  title: z.string().min(1, "Title is required").max(150),
  templateId: z.string().min(1, "Template is required"),
  frequency: z.enum(["daily", "weekly", "biweekly", "monthly", "quarterly", "semiannual", "annual", "custom"]),
  status: z.enum(["active", "paused"]),
  nextDueDate: z.string().min(1, "Next due date is required"),
  locationId: z.string().optional(),
  assetId: z.string().optional(),
  assigneeId: z.string().optional(),
  description: z.string().max(2000).optional(),
  cronExpression: z.string().optional(),
});

export type InspectionScheduleFormValues = z.infer<typeof InspectionScheduleSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Custom Field Definition
// ─────────────────────────────────────────────────────────────────────────────

export const CustomFieldDefinitionSchema = z.object({
  entityType: z.enum(["asset", "work_order", "location", "inspection_template"]),
  fieldName: z.string().min(1, "Field name is required").max(50),
  label: z.string().min(1, "Label is required").max(100),
  fieldType: z.enum(["text", "number", "date", "dropdown_single", "dropdown_multi", "boolean", "url"]),
  options: z.array(z.string()).optional(),
  isRequired: z.boolean(),
  sortOrder: z.number().int().min(0),
});

export type CustomFieldDefinitionFormValues = z.infer<typeof CustomFieldDefinitionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Part
// ─────────────────────────────────────────────────────────────────────────────

export const PartSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  sku: z.string().max(50).optional(),
  description: z.string().max(500).optional(),
  category: z.enum(["consumable", "spare_part", "tool", "safety", "other"]),
  unit: z.string().min(1, "Unit is required").max(30),
  quantityOnHand: z.coerce.number().min(0),
  reorderPoint: z.coerce.number().min(0).optional(),
  unitCost: z.coerce.number().min(0).optional(),
  locationId: z.string().optional(),
  vendorId: z.string().optional(),
});

export type PartFormValues = z.infer<typeof PartSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Vendor
// ─────────────────────────────────────────────────────────────────────────────

export const VendorSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  contactName: z.string().max(100).optional(),
  contactEmail: z.string().email("Valid email required").optional().or(z.literal("")),
  contactPhone: z.string().max(30).optional(),
  address: z.string().max(255).optional(),
  category: z.string().max(100).optional(),
  status: z.enum(["active", "inactive", "pending"]),
  licenseExpiry: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

export type VendorFormValues = z.infer<typeof VendorSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Incident
// ─────────────────────────────────────────────────────────────────────────────

export const IncidentSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).optional(),
  type: z.enum(["safety", "environmental", "equipment_failure", "quality", "near_miss", "property_damage", "security"]),
  severity: z.enum(["critical", "high", "medium", "low"]),
  status: z.enum(["reported", "investigating", "action_required", "resolved", "closed"]),
  locationId: z.string().optional(),
  assetId: z.string().optional(),
  reportedBy: z.string().min(1),
  reportedByName: z.string().min(1),
  reportedAt: z.string().min(1),
  assigneeId: z.string().optional(),
  rootCause: z.string().max(2000).optional(),
  correctiveAction: z.string().max(2000).optional(),
  resolvedAt: z.string().optional(),
  photoUrls: z.array(z.string()).optional(),
});

export type IncidentFormValues = z.infer<typeof IncidentSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Meter Reading
// ─────────────────────────────────────────────────────────────────────────────

export const MeterReadingSchema = z.object({
  assetId: z.string().min(1, "Asset is required"),
  meterType: z.enum(["runtime_hours", "odometer", "cycles", "temperature", "pressure", "energy_kwh", "custom"]),
  value: z.number().min(0, "Value must be positive"),
  unit: z.string().min(1, "Unit is required").max(30),
  readingDate: z.string().min(1, "Reading date is required"),
  recordedBy: z.string().min(1),
  recordedByName: z.string().min(1),
  notes: z.string().max(1000).optional(),
});

export type MeterReadingFormValues = z.infer<typeof MeterReadingSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Escalation Rule
// ─────────────────────────────────────────────────────────────────────────────

export const EscalationRuleSchema = z.object({
  name: z.string().min(1, "Name is required").max(150),
  description: z.string().max(500).optional(),
  entityType: z.enum(["action", "work_order", "inspection"]),
  status: z.enum(["active", "inactive"]),
  triggerType: z.enum(["overdue_hours", "overdue_days", "priority_change", "no_assignee", "status_stale"]),
  triggerValue: z.number().int().min(1, "Trigger value must be at least 1"),
  escalationAction: z.enum(["notify_manager", "reassign", "increase_priority", "create_work_order", "send_email"]),
  targetUserId: z.string().optional(),
  targetTeamId: z.string().optional(),
  priority: z.enum(["high", "medium", "low"]).optional(),
});

export type EscalationRuleFormValues = z.infer<typeof EscalationRuleSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  code: z.string().max(30).optional(),
  description: z.string().max(4000).optional(),
  type: z.enum(["planned_shutdown", "turnaround", "outage", "campaign_changeover", "regulatory_shutdown"]),
  status: z.enum(["draft", "planning", "scope_frozen", "ready", "in_execution", "startup", "completed", "closed"]),
  locationId: z.string().optional(),
  ownerId: z.string().optional(),
  plannedStartDate: z.string().min(1, "Start date is required"),
  plannedEndDate: z.string().min(1, "End date is required"),
  actualStartDate: z.string().optional(),
  actualEndDate: z.string().optional(),
  scopeFreezeDate: z.string().optional(),
  budgetEstimateRom: z.number().min(0).optional().or(z.nan().transform(() => undefined)),
  budgetEstimateDefinitive: z.number().min(0).optional().or(z.nan().transform(() => undefined)),
  budgetActual: z.number().min(0).optional().or(z.nan().transform(() => undefined)),
  currency: z.string().max(3).optional(),
  gmpCompliance: z.boolean(),
  qualityHoldPoints: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
});

export type TaskPlanFormValues = z.infer<typeof TaskPlanSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Scope Item
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanScopeItemSchema = z.object({
  taskPlanId: z.string().min(1, "Task plan is required"),
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).optional(),
  source: z.enum(["inspection", "action", "work_order", "pm_schedule", "operations", "regulatory", "engineering", "incident", "other"]),
  sourceEntityId: z.string().optional(),
  sourceEntityType: z.string().optional(),
  status: z.enum(["proposed", "under_review", "approved", "deferred", "rejected", "in_work_package"]),
  priority: z.enum(["critical", "high", "medium", "low"]),
  assetId: z.string().optional(),
  locationId: z.string().optional(),
  estimatedHours: z.number().min(0).optional(),
  estimatedCost: z.number().min(0).optional(),
  justification: z.string().max(2000).optional(),
  riskIfDeferred: z.string().max(2000).optional(),
  gmpImpact: z.boolean(),
});

export type TaskPlanScopeItemFormValues = z.infer<typeof TaskPlanScopeItemSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Work Package
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanWorkPackageSchema = z.object({
  taskPlanId: z.string().min(1, "Task plan is required"),
  parentId: z.string().optional(),
  code: z.string().min(1, "WBS code is required").max(20),
  name: z.string().min(1, "Name is required").max(200),
  description: z.string().max(4000).optional(),
  type: z.enum(["mechanical", "electrical", "instrumentation", "civil", "cleaning", "inspection", "calibration", "qualification", "isolation", "startup", "other"]),
  status: z.enum(["draft", "planned", "resourced", "material_ready", "ready", "in_progress", "completed", "quality_hold", "closed"]),
  assetId: z.string().optional(),
  locationId: z.string().optional(),
  leadId: z.string().optional(),
  vendorId: z.string().optional(),
  plannedStartDate: z.string().optional(),
  plannedEndDate: z.string().optional(),
  estimatedLaborHours: z.number().min(0),
  estimatedCost: z.number().min(0).optional(),
  crewSize: z.number().int().min(0).optional(),
  predecessorIds: z.array(z.string()).optional(),
  isolationRequired: z.boolean(),
  permitRequired: z.boolean(),
  permitType: z.string().max(100).optional(),
  qualityHoldRequired: z.boolean(),
  checklistTemplateId: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export type TaskPlanWorkPackageFormValues = z.infer<typeof TaskPlanWorkPackageSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Material
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanMaterialSchema = z.object({
  workPackageId: z.string().min(1, "Work package is required"),
  taskPlanId: z.string().min(1, "Task plan is required"),
  partId: z.string().optional(),
  description: z.string().min(1, "Description is required").max(500),
  quantity: z.number().min(0.01, "Quantity must be positive"),
  unit: z.string().min(1, "Unit is required").max(30),
  estimatedUnitCost: z.number().min(0).optional(),
  status: z.enum(["identified", "ordered", "received", "kitted", "issued", "returned"]),
  vendorId: z.string().optional(),
  purchaseOrderRef: z.string().max(50).optional(),
  expectedDeliveryDate: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

export type TaskPlanMaterialFormValues = z.infer<typeof TaskPlanMaterialSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Milestone
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanMilestoneSchema = z.object({
  taskPlanId: z.string().min(1, "Task plan is required"),
  name: z.string().min(1, "Name is required").max(150),
  description: z.string().max(1000).optional(),
  type: z.enum(["phase_gate", "scope_freeze", "material_cutoff", "mobilization", "shutdown_start", "quality_hold", "startup_begin", "production_resume", "closeout", "custom"]),
  status: z.enum(["pending", "on_track", "at_risk", "missed", "completed"]),
  plannedDate: z.string().min(1, "Planned date is required"),
  responsibleId: z.string().optional(),
  isGate: z.boolean(),
  gateApprovalRequired: z.boolean(),
  sortOrder: z.number().int().min(0),
  linkedWorkPackageIds: z.array(z.string()).optional(),
  notes: z.string().max(2000).optional(),
});

export type TaskPlanMilestoneFormValues = z.infer<typeof TaskPlanMilestoneSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Risk
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanRiskSchema = z.object({
  taskPlanId: z.string().min(1, "Task plan is required"),
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).optional(),
  category: z.enum(["safety", "quality", "schedule", "cost", "environmental", "regulatory", "resource", "technical"]),
  status: z.enum(["identified", "mitigating", "accepted", "resolved", "occurred"]),
  likelihood: z.enum(["rare", "unlikely", "possible", "likely", "almost_certain"]),
  consequence: z.enum(["negligible", "minor", "moderate", "major", "catastrophic"]),
  mitigationPlan: z.string().max(4000).optional(),
  contingencyPlan: z.string().max(4000).optional(),
  ownerId: z.string().optional(),
  residualLikelihood: z.enum(["rare", "unlikely", "possible", "likely", "almost_certain"]).optional(),
  residualConsequence: z.enum(["negligible", "minor", "moderate", "major", "catastrophic"]).optional(),
  linkedWorkPackageIds: z.array(z.string()).optional(),
  linkedAssetIds: z.array(z.string()).optional(),
  dueDate: z.string().optional(),
  notes: z.string().max(2000).optional(),
});

export type TaskPlanRiskFormValues = z.infer<typeof TaskPlanRiskSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Task Plan Daily Log
// ─────────────────────────────────────────────────────────────────────────────

export const TaskPlanDailyLogSchema = z.object({
  taskPlanId: z.string().min(1, "Task plan is required"),
  logDate: z.string().min(1, "Log date is required"),
  shift: z.enum(["day", "night", "swing"]).optional(),
  authorId: z.string().min(1, "Author is required"),
  summary: z.string().min(1, "Summary is required").max(4000),
  plannedActivities: z.string().max(4000).optional(),
  actualActivities: z.string().max(4000).optional(),
  issuesEncountered: z.string().max(4000).optional(),
  decisionsRecorded: z.string().max(4000).optional(),
  safetyObservations: z.string().max(4000).optional(),
  qualityObservations: z.string().max(4000).optional(),
  headcount: z.number().int().min(0).optional(),
  scheduleCompliancePercent: z.number().min(0).max(100).optional(),
});

export type TaskPlanDailyLogFormValues = z.infer<typeof TaskPlanDailyLogSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// API Key (Integration)
// ─────────────────────────────────────────────────────────────────────────────

export const ApiKeySchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  scopes: z.array(z.string()).min(1, "At least one scope is required"),
  rateLimit: z.number().int().min(1).max(100000),
  expiresAt: z.string().optional(),
  isActive: z.boolean(),
});

export type ApiKeyFormValues = z.infer<typeof ApiKeySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Webhook Subscription (Integration)
// ─────────────────────────────────────────────────────────────────────────────

export const WebhookSubscriptionSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  url: z.string().url("Valid URL is required"),
  events: z.array(z.string()).min(1, "At least one event is required"),
  isActive: z.boolean(),
});

export type WebhookSubscriptionFormValues = z.infer<typeof WebhookSubscriptionSchema>;
